import { useState, useEffect } from 'react';
import { Container, Box, Button, TextField, MenuItem, FormControlLabel, Checkbox } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IUserUpdateData } from '../../../models/IUser';
import { updateUserProfileAPI } from '../../../api/AuthAPI';
import PhoneInput from 'react-phone-input-2';
// @ts-ignore: imported for side effects, no type declarations for CSS module
import 'react-phone-input-2/lib/material.css';
import { PATH_NAME } from '../../../configs/pathName';
import useSWR from 'swr';
import { generalFetcher } from '../../../helpers/Fetchers/fetchers';
import { typo } from '../../../assets/fonts/font';
import colorConfigs from '../../../configs/colorConfigs';

const EditProfile = () => {
  const navigate = useNavigate();

  const { data: basicUserInfo } = useSWR(PATH_NAME.API_AUTH_PROFILE, generalFetcher);

  const [profileData, setProfileData] = useState<IUserUpdateData>({
    firstname: '',
    lastname: '',
    birth_year: 0,
    birth_month: 0,
    email: '',
    mobile: '',
    country_code: '',
    full_mobile: '',
    email_consent: false,
    sms_consent: false, 
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (basicUserInfo) {
      setProfileData({
        ...basicUserInfo,
      });
    }
  }, [basicUserInfo]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setProfileData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleSaveChanges = async () => {
    if (profileData.email_consent && (profileData.email?.trim() === "" || profileData.email === undefined)) {
      alert("Email is required when email consent is given.");
      return;
    }

    setIsLoading(true);
    try {
      const { country_code, mobile, full_mobile, ...updatableFields } = profileData;
      await updateUserProfileAPI({
        ...updatableFields,
        mobile,
        country_code,
        full_mobile,
      });
      localStorage.setItem('userInfo', JSON.stringify(profileData));
      navigate(PATH_NAME.PROFILE);
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container sx={{ maxWidth: '400px', padding: 4, backgroundColor: 'var(--color-white)', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 3 }}>
      </Box>
      
      <TextField
        label="First Name"
        value={profileData.firstname}
        onChange={(e) => setProfileData({ ...profileData, firstname: e.target.value })}
        fullWidth
        margin="normal"
        InputProps={{ sx: { input: { ...typo.bodyContent, }, }, }}
        InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              ...typo.bodyContent,
            },
            "&.Mui-focused fieldset": {
              borderColor: colorConfigs.primary,
              ...typo.bodyContent,
            },
          },
          "& .MuiInputLabel-root": {},
          "& .MuiInputLabel-root.Mui-focused": {
            ...typo.bodyContent,
          },
        }}
      />
      <TextField
        label="Last Name"
        value={profileData.lastname}
        onChange={(e) => setProfileData({ ...profileData, lastname: e.target.value })}
        fullWidth
        margin="normal"
        InputProps={{ sx: { input: { ...typo.bodyContent, }, }, }}
        InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              ...typo.bodyContent,
            },
            "&.Mui-focused fieldset": {
              borderColor: colorConfigs.primary,
              ...typo.bodyContent,
            },
          },
          "& .MuiInputLabel-root": {},
          "& .MuiInputLabel-root.Mui-focused": {
            ...typo.bodyContent,
          },
        }}
      />
      <TextField
        label="Email"
        value={profileData.email}
        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
        fullWidth
        margin="normal"
        InputProps={{ sx: { input: { ...typo.bodyContent, }, }, }}
        InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              ...typo.bodyContent,
            },
            "&.Mui-focused fieldset": {
              borderColor: colorConfigs.primary,
              ...typo.bodyContent,
            },
          },
          "& .MuiInputLabel-root": {},
          "& .MuiInputLabel-root.Mui-focused": {
            ...typo.bodyContent,
          },
        }}
      />
      
      <PhoneInput
        value={profileData.full_mobile}
        containerStyle={{
          width: "100%",
          ...typo.bodyContent,
        }}
        inputStyle={{
          width: "100%",
        }}
        disabled
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <TextField
          select
          label="Birth Year"
          value={profileData.birth_year}
          onChange={(e) => setProfileData({ ...profileData, birth_year: parseInt(e.target.value) })}
          fullWidth
          margin="normal"
          InputProps={{ sx: { input: { ...typo.bodyContent, }, }, }}
          InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
          sx={{
            "& .MuiOutlinedInput-root": {
              ...typo.bodyContent,
              "& fieldset": {
                ...typo.bodyContent,
              },
              "&.Mui-focused fieldset": {
                borderColor: colorConfigs.primary,
              },
              "& .MuiOutlinedInput-input": {
                ...typo.bodyContent,
              },
            },
            "& .MuiInputLabel-root": {
              ...typo.bodyContent,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              ...typo.bodyContent,
            },
          }}
        >
          {[...Array(100)].map((_, i) => (
            <MenuItem
              key={i}
              value={new Date().getFullYear() - i}
              sx={{
                ...typo.bodyContent, // Apply styles to each dropdown item
              }}
            >
              {new Date().getFullYear() - i}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Birth Month"
          value={profileData.birth_month}
          onChange={(e) => setProfileData({ ...profileData, birth_month: parseInt(e.target.value) })}
          fullWidth
          margin="normal"
          InputProps={{ sx: { input: { ...typo.bodyContent, }, }, }}
          InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
          sx={{
            "& .MuiOutlinedInput-root": {
              ...typo.bodyContent,
              "& fieldset": {
                ...typo.bodyContent,
              },
              "&.Mui-focused fieldset": {
                borderColor: colorConfigs.primary,
              },
              "& .MuiOutlinedInput-input": {
                ...typo.bodyContent,
              },
            },
            "& .MuiInputLabel-root": {
              ...typo.bodyContent,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              ...typo.bodyContent,
            },
          }}
        >
          {[...Array(12)].map((_, i) => (
            <MenuItem
              key={i}
              value={i + 1}
              sx={{
                ...typo.bodyContent, // Apply styles to each dropdown item
              }}
            >
              {i + 1}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={profileData.email_consent}
            onChange={handleCheckboxChange}
            name="email_consent"
          />
        }
        label="Email Consent"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={profileData.sms_consent}
            onChange={handleCheckboxChange}
            name="sms_consent"
          />
        }
        label="SMS Consent"
      />

      <Button
        variant="contained"
        fullWidth
        onClick={handleSaveChanges}
        disabled={isLoading}
        sx={{ 
          ...typo.button,
          marginTop: 3,
          backgroundColor: colorConfigs.primary,
          color: colorConfigs.secondary,
          '&:hover': { backgroundColor: colorConfigs.primaryHover },
        }}
      >
        Update Profile
      </Button>
    </Container>
  );
};

export default EditProfile;